import React, { useCallback, useState } from 'react';

import usePageTitle from '../../../utils/hooks/use_page_title';

import BasicDropdown from '../../common/BasicDropdown/BasicDropdown';
import ErrorSlate from '../../ErrorSlate/ErrorSlate';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import PageLayout from '../../pages/PageLayout';
import PrimaryContent from '../../pages/layouts/PrimaryContent';
import SearchInput from '../../common/TextInputs/SearchInput/SearchInput';

import {
  OrchestrationSearchProvider,
  useOrchestrationSearch,
} from '../OrchestrationSearchContext/OrchestrationSearchContext';
import OrchestrationBlankSlate from '../OrchestrationBlankSlate/OrchestrationBlankSlate';
import OrchestrationTable from '../OrchestrationTable/OrchestrationTable';

const statusItems = [
  {
    id: 'all',
    label: 'All Workloads'
  },
  {
    id: 'active',
    label: 'Only Active',
  },
  {
    id: 'inactive',
    label: 'Only Inactive',
  },
  {
    id: 'error',
    label: 'Only Errors',
  },
]

const StatusFilterDropdown = ({
  filteredStatus, onFilterChange
}) => (
  <BasicDropdown
    dropdownLabel=""
    values={statusItems}
    selectedValue={filteredStatus}
    onValueSelected={onFilterChange}
  />
);

const filterOrchestrationsByStatus = (orchestrations, filteredStatus) => {
  if (!orchestrations || !orchestrations?.length || !filteredStatus || filteredStatus === 'all') {
    return orchestrations;
  }

  return orchestrations.filter(({ status }) => {
    // Ready as Active
    if (filteredStatus === 'active') {
      return status === 'ready';
    }
    if (filteredStatus === 'error') {
      return status === 'error';
    }

    // Packaging, Pending, Uninitialized - All treated as Inactive
    return status !== 'ready' && status !== 'error';
  });
}

const filterOrchestrationsBySearch = (orchestrations, searchStr) => {
  if (!orchestrations || !orchestrations?.length || !searchStr?.length) {
    return orchestrations;
  }
  return orchestrations.filter(({ name, id }) => {
    const lowerCaseSearchStr = searchStr.toLowerCase();
    const matchesName = name && name.toLowerCase().includes(lowerCaseSearchStr);
    const matchesId = id && id.toLowerCase().includes(lowerCaseSearchStr);
    return matchesName || matchesId;
  })
}

export const OrchestrationListView = ({
  orchestrations,
  isLoading,
  hasError,
}) => (
  <div className="mt-2 bg-white border border-border-dark rounded relative">
    {
      isLoading ? (
        <LoadingSpinner animationKey="orchestration-list-spinner" size="medium" useOverlay />
      ) : null
    }
    {
      hasError ? (
        <ErrorSlate
          errorText="Something went wrong loading your orchestrations"
        />
      ) : !orchestrations?.length ? (
        <OrchestrationBlankSlate />
      ) : (
        <OrchestrationTable
          orchestrationData={orchestrations}
        />
      )
    }
  </div>
)

export const OrchestrationSearchPanel = () => {
  const {
    orchestrations,
    loading,
    error,
    searchQuery,
    setSearchQuery,
  } = useOrchestrationSearch();

  const [filteredStatus, setFilteredStatus] = useState('all');
  const filteredOrchestrations = filterOrchestrationsBySearch(filterOrchestrationsByStatus(orchestrations, filteredStatus), searchQuery);

  return (
    <div>
      <div className="w-full flex flex-grow-2 justify-start items-center h-full">
        <SearchInput
          className="relative flex-grow border border-border-dark rounded py-2 pl-10 mr-2 bg-white"
          ariaLabel="Search by workload name"
          placeholder="Search by workload name"
          value={searchQuery}
          onChange={event => setSearchQuery(event.target.value)}
        />
        <StatusFilterDropdown filteredStatus={filteredStatus} onFilterChange={setFilteredStatus} />
      </div>
      <div className="mt-2">
        <OrchestrationListView
          orchestrations={filteredOrchestrations}
          isLoading={loading}
          hasError={error}
        />
      </div>
    </div>
  )
}

export const OrchestrationsIndex = () => {
  usePageTitle('Orchestrations | Wallaroo');

  return (
    <PageLayout>
      <OrchestrationSearchProvider>
        <PrimaryContent>
          <OrchestrationSearchPanel />
        </PrimaryContent>
      </OrchestrationSearchProvider>
    </PageLayout>
  );
};

export default OrchestrationsIndex;
